import React, { Fragment, useState, useEffect, useCallback, useContext } from 'react';
import { Form, Field } from 'react-final-form';
import { connect } from 'react-redux';
import axios from 'axios';
import { useAlert } from 'react-alert';

import errorHandler from '../../../../../../../../hoc/ErrorHandler';
import context from '../../../../../../../../context/auth-context';
import ColorPicker from '../../../../../../../UI/ColorPicker/ColorPicker';
import ImageCropper from '../../../../../../../UI/ImageCropper/ImageCropper';
import PhoneNumberInput from '../../../../../../../UI/PhoneNumberInput/PhoneNumberInput';
import paneBackground from '../../../../../../../../assets/images/placeholder.png';
import preloader from '../../../../../../../../assets/images/preloader.gif';
import SlidingPane from '../../../../../../../UI/SlidingPane/SlidingPane';

const CompanyForm = props => {
    const [logoCropperOpen, setLogoCropperOpen] = useState(false);
    const [companyLogo, setCompanyLogo] = useState(null);

    const authContext = useContext(context);
    const alert = useAlert();
    const companyToEdit = useCallback(() => props.companyToEdit, [props.companyToEdit]);

    const companies = useCallback(() => props.companies.data, [props.companies.data]);
    const error = useCallback(() => props.companies.error, [props.companies.error]);

    useEffect(() => {
        const getCompanyLogo = async () => {
            const URI = await axios.get(`/companies/${companyToEdit().id}/logo`, {headers: {
                'Company-Code': authContext.companyCode
            }, responseType: 'blob'});
    
            if(URI) {
                const urlCreator = window.URL || window.webkitURL;
                setCompanyLogo(urlCreator.createObjectURL(URI.data));
            }else {
                alert.show('Error fetching company logo', {type: 'error'});
            }
        };

        if(companyToEdit() && !companyLogo) getCompanyLogo();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyToEdit]);

    const timeZone = useCallback(() => {
        if(props.geoInfo && props.timeZones) 
            return props.timeZones.filter(zone => zone.name === props.geoInfo.timezone || zone.name.includes(props.geoInfo.city));
        return null;
    }, [props.geoInfo, props.timeZones]);

    const newCompanyDefaultValues = {
        country: props.geoInfo && props.geoInfo.country_name,
        city: props.geoInfo && props.geoInfo.city,
        time_zone: timeZone() && timeZone()[0].id,
        session_timeout: 60,
        maximum_users:  15
    };

    const existingCompanyDefaultValues = {
        name: companyToEdit()?.name,
        short_name: companyToEdit()?.short_name,
        parent_company: companyToEdit()?.parent_id,
        country: companyToEdit()?.country,
        city: companyToEdit()?.city,
        time_zone: companyToEdit()?.time_zone?.id,
        session_timeout: companyToEdit()?.session_timeout,
        maximum_users: companyToEdit()?.maximum_users,
        email: companyToEdit()?.email
    };

    const logoCropHandler = logo => {
        if(companyToEdit()) setCompanyLogo(logo);
        setLogoCropperOpen(false);
        return props.logoHandler(logo);
    };

    return (
        <Fragment>
            <Form onSubmit={props.submitHandler} initialValues={companyToEdit() ? existingCompanyDefaultValues : newCompanyDefaultValues} render={({handleSubmit, form, submitting, pristine, values}) => (
                <form autoComplete="off" onSubmit={handleSubmit} style={{marginBottom: '45px'}}>
                    {companyToEdit() && parseInt(companyToEdit().id) === authContext.companyId ? null : (
                        <header className="slidePanel-header overlay" style={{backgroundImage: `url(${paneBackground})`, textAlign: 'center', width: '100%', height: '350px', overflow: 'inherit'}}>
                            <div className="overlay-panel overlay-background vertical-align">
                                <div className="vertical-align-middle">
                                    <div style={{position: 'relative', display: 'inline-block', whiteSpace: 'nowrap', verticalAlign: 'bottom'}}>
                                        <label className="contact-avatar-btn" onClick={() => setLogoCropperOpen(true)}>
                                            <span className="icon fa-camera"></span>
                                        </label>
                                        <img className="img-fluid mx-auto d-block" alt="Company Logo" src={companyToEdit() ? (!companyLogo ? preloader : companyLogo) : props.companyLogo} style={{maxHeight: '200px'}} />
                                    </div>
                                    <h3 className="name">Company logo</h3>
                                </div>
                            </div>
                        </header>
                    )}
                    <div className="slidePanel-inner" style={{marginTop: '10px'}}>
                        <div className="row">
                            <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                <label className="form-control-label" htmlFor="name">Name</label>
                                <Field type="text" id="name" name="name" className="form-control" component="input" required />
                            </div>
                            <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                <label className="form-control-label" htmlFor="short_name">Short Name</label>
                                <Field type="text" id="short_name" name="short_name" className="form-control" component="input" required />
                            </div>
                        </div>
                        <div className="row">
                            <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                <Fragment>
                                    <label className="form-control-label" htmlFor="time_zone">Time Zone</label>
                                    <Field id="time_zone" className="form-control" name="time_zone" component="select" required>
                                        <optgroup label="Select this companies's time zone">
                                            <option></option>
                                            {props.timeZones?.map(zone => <option key={zone.id} value={zone.id}>{zone.name}</option>)}
                                        </optgroup>
                                    </Field>
                                </Fragment>
                            </div>
                            <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                <label className="form-control-label" htmlFor="session_timeout">Session time out (minutes)</label>
                                <Field type="number" id="session_timeout" name="session_timeout" className="form-control" component="input" required />
                            </div>
                        </div>
                        <div className="row">
                            <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                {!error() ? (
                                    <Fragment>
                                        <label className="form-control-label" htmlFor="parent_company">Parent Company</label>
                                        <Field id="parent_company" className="form-control" name="parent_company" component="select" disabled={companyToEdit() && !companyToEdit().parent_id}>
                                            <optgroup label="Select this companies's parent">
                                                <option></option>
                                                {companies()?.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}
                                            </optgroup>
                                        </Field>
                                    </Fragment>
                                ) : <p style={{fontSize: '18px', color: '#f44336'}}>Error fetching companies</p>}
                            </div>
                            <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                <label className="form-control-label" htmlFor="maximum_users">Maximum number of users</label>
                                <Field type="number" id="maximum_users" name="maximum_users" className="form-control" component="input" required />
                            </div>
                        </div>
                        <div className="row">
                            <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                <label className="form-control-label" htmlFor="country">Country</label>
                                <Field type="text" id="country" name="country" className="form-control" component="input" required />
                            </div>
                            <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                <label className="form-control-label" htmlFor="city">City</label>
                                <Field type="text" id="city" name="city" className="form-control" component="input" required />
                            </div>
                        </div>
                        {companyToEdit() && (
                            <div className="row">
                                <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                    <label className="form-control-label" htmlFor="Phone">Phone</label>
                                    <PhoneNumberInput phoneChangeHandler={value => props.companyPhoneChangeHandler(value)} value={props.companyPhone} />
                                </div>
                                <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                    <label className="form-control-label" htmlFor="email">Email</label>
                                    <Field type="email" id="email" name="email" className="form-control" component="input" />
                                </div>
                            </div>
                        )}
                        <div className="row">
                            <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                <label className="form-control-label" htmlFor="primary_color">Primary Color</label>
                                <ColorPicker initialColor={props.colors.primary} colorHandler={color => props.primaryColorChangeHandler(color.hex)} />
                            </div>
                            <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                <label className="form-control-label" htmlFor="secondary_color">Secondary Color</label>
                                <ColorPicker initialColor={props.colors.secondary} colorHandler={color => props.secondaryColorChangeHandler(color.hex)} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                <label className="form-control-label" htmlFor="tertiary_color">Tertiary Color</label>
                                <ColorPicker initialColor={props.colors.tertiary} colorHandler={color => props.tertiaryColorChangeHandler(color.hex)} />
                            </div>
                            <div className="form-group form-material col-md-6" data-plugin="formMaterial">
                                <label className="form-control-label" htmlFor="auxilliary_color">Auxilliary Color</label>
                                <ColorPicker initialColor={props.colors.auxilliary} colorHandler={color => props.auxilliaryColorChangeHandler(color.hex)} />
                            </div>
                        </div>
                        <div style={{textAlign: 'center', margin: '25px auto'}}>
                            <button type="submit" className="btn btn-raised btn-animate btn-animate-vertical btn-primary btn-sm waves-effect waves-classic" disabled={submitting || pristine}>
                                <span><i className="icon pe-diskette" aria-hidden="true"></i>&nbsp;Submit</span>
                            </button>
                            &nbsp;
                            <button type="button" className="btn btn-raised btn-animate btn-animate-vertical btn-default btn-sm waves-effect waves-classic" onClick={form.reset} disabled={submitting || pristine}>
                                <span><i className="icon ti-reload" aria-hidden="true"></i>&nbsp;Clear</span>
                            </button>
                        </div>
                        {/* <pre>{JSON.stringify(values, 0, 2)}</pre> */}
                    </div>
                </form>
            )}/>
            <SlidingPane open={logoCropperOpen} 
                title={!companyToEdit() ? 'Company logo' : companyToEdit().name + ' logo'} 
                subtitle="Crop company logo"
                onRequestClose={() => setLogoCropperOpen(false)}>
                <ImageCropper defaultImage={companyLogo ?? props.companyLogo} imageCropHandler={logo => logoCropHandler(logo)} />
            </SlidingPane>
        </Fragment>
    );
};

const mapStateToProps = state => {
    return {
        companies: state.utilities.companies,
        geoInfo: state.utilities.geoInfo,
        timeZones: state.utilities.timeZones
    };
};

export default connect(mapStateToProps)(errorHandler(CompanyForm, axios));